import {BaseApi} from './BaseApi'

/**
 * Paystub Status Enum
 */
export enum PaystubStatus {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
  Voided = 'voided',
}

/**
 * Paystub Earning Response
 */
export interface PaystubEarning {
  paycodeId: string
  paycodeName: string
  systemCode: string
  units: number
  unitType: string
  rate: number
  amount: number
}

/**
 * Paystub Tax Response
 */
export interface PaystubTax {
  taxId: string
  taxType: string
  taxDescription: string
  amount: number
}

/**
 * Paystub Deduction Response
 */
export interface PaystubDeduction {
  code: string
  description: string
  legalCategory: string
  isPreTax: boolean
  calculatedAmount: number
  actualAmount: number
  shortfallAmount: number
  ytdAmount: number
}

/**
 * Paystub Garnishment Response
 */
export interface PaystubGarnishment {
  type: string
  caseNumber?: string
  calculatedAmount: number
  actualAmount: number
  ytdAmount?: number
  remainingBalance?: number
}

/**
 * Paystub Response - Full paystub data
 */
export interface PaystubResponse {
  _id: string
  invoiceId: string
  invoiceItemId: string
  batchId: string
  applicantId: string
  agencyId: string
  companyId: string
  assignmentId?: string
  employeeName: string
  firstName: string
  lastName: string
  ssnLast4: string
  employeeNumber?: string
  payPeriodStart: string
  payPeriodEnd: string
  payDate: string
  payFrequency: string
  status: PaystubStatus
  calculatedAt: string
  approvedAt?: string
  approvedBy?: string
  rejectionReason?: string
  // Earnings
  earnings: PaystubEarning[]
  grossWages: number
  // Taxes
  employeeTaxes: PaystubTax[]
  totalEmployeeTaxes: number
  totalEmployerTaxes: number
  // Deductions
  preTaxDeductions: PaystubDeduction[]
  totalPreTaxDeductions: number
  postTaxDeductions: PaystubDeduction[]
  totalPostTaxDeductions: number
  // Garnishments
  garnishments: PaystubGarnishment[]
  totalGarnishments: number
  // Net Pay
  netPay: number
  paymentMethod: string
  checkNumber?: string
  // YTD Totals
  ytdGrossWages: number
  ytdFederalTax: number
  ytdStateTax: number
  ytdFICA: number
  ytdMedicare: number
  ytdSDI: number
  ytdNetPay: number
  // Other
  locationCode?: string
  calculationError?: string
  notes?: string
  paidAt?: string
  paidBy?: string
  externalPayrollBatchId?: string
  createdDate: string
  modifiedDate: string
}

/**
 * Paystub List Response with pagination
 */
export interface PaystubListResponse {
  paystubs: PaystubResponse[]
  total: number
  page: number
  limit: number
  totalPages: number
}

/**
 * Paystub Summary Response
 */
export interface PaystubSummaryResponse {
  totalPaystubs: number
  pendingCount: number
  approvedCount: number
  rejectedCount: number
  voidedCount: number
  totalGrossWages: number
  totalEmployeeTaxes: number
  totalEmployerTaxes: number
  totalNetPay: number
}

/**
 * Paystub Filter Options
 */
export interface PaystubFilters {
  id?: string
  invoiceId?: string
  batchId?: string
  applicantId?: string
  agencyId?: string
  companyId?: string
  status?: PaystubStatus
  payPeriodStartFrom?: string
  payPeriodStartTo?: string
  page?: number
  limit?: number
}

/**
 * Approve Paystub Request
 */
export interface ApprovePaystubRequest {
  notes?: string
}

/**
 * Reject Paystub Request
 */
export interface RejectPaystubRequest {
  rejectionReason: string
}

/**
 * Void Paystub Request
 */
export interface VoidPaystubRequest {
  reason: string
}

/**
 * Update Paystub Notes Request
 */
export interface UpdatePaystubNotesRequest {
  notes: string
}

/**
 * Create Paystub Deduction Request
 * Based on backend CreatePaystubDeductionRequest DTO
 */
export interface CreatePaystubDeductionRequest {
  paycodeId: string // Required - the paycode ID to use
  amount: number // Required - the deduction amount
  caseNumber?: string // Optional - required for garnishments
}

/**
 * Update Paystub Deduction Request
 * Based on backend UpdatePaystubDeductionRequest DTO
 */
export interface UpdatePaystubDeductionRequest {
  amount?: number
  caseNumber?: string
}

/**
 * Deduction Response from API
 */
export interface DeductionResponse {
  id: string
  paystubId: string
  paycodeId: string
  code: string
  description: string
  legalCategory: string
  isPreTax: boolean
  amount: number
  caseNumber?: string
  ytdAmount: number
  createdDate: string
  modifiedDate: string
}

/**
 * Paystubs API Service
 * Handles all paystub-related API calls
 * Extends BaseApi for automatic Bearer token authorization
 */
export class PaystubsApi extends BaseApi {
  // ==================== HEALTH CHECK ====================

  /**
   * Health check for paystub routes
   * GET /payroll/paystubs/health
   */
  async healthCheck(): Promise<{status: string}> {
    try {
      const response = await this.client.get<{status: string}>('/payroll/paystubs/health')
      return response.data
    } catch (error: unknown) {
      console.error('Error checking paystubs health:', error)
      throw error
    }
  }

  // ==================== GET PAYSTUBS ====================

  /**
   * Get paystubs with filters and pagination
   * GET /payroll/paystubs
   *
   * @param filters - Optional filter parameters
   */
  async getPaystubs(filters?: PaystubFilters): Promise<PaystubListResponse> {
    try {
      const params = new URLSearchParams()
      if (filters?.id) params.append('id', filters.id)
      if (filters?.invoiceId) params.append('invoiceId', filters.invoiceId)
      if (filters?.batchId) params.append('batchId', filters.batchId)
      if (filters?.applicantId) params.append('applicantId', filters.applicantId)
      if (filters?.agencyId) params.append('agencyId', filters.agencyId)
      if (filters?.companyId) params.append('companyId', filters.companyId)
      if (filters?.status) params.append('status', filters.status)
      if (filters?.payPeriodStartFrom)
        params.append('payPeriodStartFrom', filters.payPeriodStartFrom)
      if (filters?.payPeriodStartTo) params.append('payPeriodStartTo', filters.payPeriodStartTo)
      if (filters?.page) params.append('page', filters.page.toString())
      if (filters?.limit) params.append('limit', filters.limit.toString())

      const response = await this.client.get<PaystubListResponse>(
        `/payroll/paystubs${params.toString() ? `?${params.toString()}` : ''}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching paystubs:', filters, error)
      throw error
    }
  }

  /**
   * Get paystub summary for an invoice
   * GET /payroll/paystubs/invoice/:invoiceId/summary
   *
   * @param invoiceId - The invoice ID
   */
  async getPaystubSummary(invoiceId: string): Promise<PaystubSummaryResponse> {
    try {
      const response = await this.client.get<PaystubSummaryResponse>(
        `/payroll/paystubs/invoice/${invoiceId}/summary`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching paystub summary:', invoiceId, error)
      throw error
    }
  }

  /**
   * Get all paystubs for an invoice
   * GET /payroll/paystubs/invoice/:invoiceId
   *
   * @param invoiceId - The invoice ID
   */
  async getPaystubsByInvoice(invoiceId: string): Promise<PaystubResponse[]> {
    try {
      const response = await this.client.get<PaystubResponse[]>(
        `/payroll/paystubs/invoice/${invoiceId}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching paystubs for invoice:', invoiceId, error)
      throw error
    }
  }

  /**
   * Get paystub by ID
   * GET /payroll/paystubs/:paystubId
   *
   * @param paystubId - The paystub ID
   */
  async getPaystubById(paystubId: string): Promise<PaystubResponse> {
    try {
      const response = await this.client.get<PaystubResponse>(`/payroll/paystubs/${paystubId}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching paystub:', paystubId, error)
      throw error
    }
  }

  // ==================== PAYSTUB ACTIONS ====================

  /**
   * Approve all pending paystubs for an invoice
   * POST /payroll/paystubs/invoice/:invoiceId/approve-all
   *
   * @param invoiceId - The invoice ID
   */
  async approveAllPaystubs(invoiceId: string): Promise<{approved: number; failed: number}> {
    try {
      const response = await this.client.post<{approved: number; failed: number}>(
        `/payroll/paystubs/invoice/${invoiceId}/approve-all`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error approving all paystubs:', invoiceId, error)
      throw error
    }
  }

  /**
   * Approve a pending paystub
   * POST /payroll/paystubs/:paystubId/approve
   *
   * @param paystubId - The paystub ID
   * @param request - Optional notes
   */
  async approvePaystub(
    paystubId: string,
    request?: ApprovePaystubRequest,
  ): Promise<PaystubResponse> {
    try {
      const response = await this.client.post<PaystubResponse>(
        `/payroll/paystubs/${paystubId}/approve`,
        request || {},
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error approving paystub:', paystubId, error)
      throw error
    }
  }

  /**
   * Reject a pending paystub
   * POST /payroll/paystubs/:paystubId/reject
   *
   * @param paystubId - The paystub ID
   * @param request - Rejection reason
   */
  async rejectPaystub(paystubId: string, request: RejectPaystubRequest): Promise<PaystubResponse> {
    try {
      const response = await this.client.post<PaystubResponse>(
        `/payroll/paystubs/${paystubId}/reject`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error rejecting paystub:', paystubId, error)
      throw error
    }
  }

  /**
   * Void an approved paystub
   * POST /payroll/paystubs/:paystubId/void
   *
   * @param paystubId - The paystub ID
   * @param request - Void reason
   */
  async voidPaystub(paystubId: string, request: VoidPaystubRequest): Promise<PaystubResponse> {
    try {
      const response = await this.client.post<PaystubResponse>(
        `/payroll/paystubs/${paystubId}/void`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error voiding paystub:', paystubId, error)
      throw error
    }
  }

  /**
   * Update paystub notes
   * PATCH /payroll/paystubs/:paystubId/notes
   *
   * @param paystubId - The paystub ID
   * @param request - Notes to update
   */
  async updatePaystubNotes(
    paystubId: string,
    request: UpdatePaystubNotesRequest,
  ): Promise<PaystubResponse> {
    try {
      const response = await this.client.patch<PaystubResponse>(
        `/payroll/paystubs/${paystubId}/notes`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error updating paystub notes:', paystubId, error)
      throw error
    }
  }

  /**
   * Recalculate taxes for a pending paystub
   * POST /payroll/paystubs/:paystubId/recalculate
   *
   * @param paystubId - The paystub ID
   */
  async recalculatePaystub(paystubId: string): Promise<PaystubResponse> {
    try {
      const response = await this.client.post<PaystubResponse>(
        `/payroll/paystubs/${paystubId}/recalculate`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error recalculating paystub:', paystubId, error)
      throw error
    }
  }

  // ==================== DEDUCTION MANAGEMENT ====================

  /**
   * Add a deduction to a paystub
   * POST /payroll/paystubs/:paystubId/deductions
   *
   * The backend determines whether it's a pre-tax, post-tax, or garnishment
   * deduction based on the paycode's legalCategory.
   *
   * @param paystubId - The paystub ID
   * @param request - The deduction data (paycodeId, amount, optional caseNumber)
   */
  async addDeduction(
    paystubId: string,
    request: CreatePaystubDeductionRequest,
  ): Promise<DeductionResponse> {
    try {
      const response = await this.client.post<DeductionResponse>(
        `/payroll/paystubs/${paystubId}/deductions`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error adding deduction to paystub:', paystubId, error)
      throw error
    }
  }

  /**
   * Update a deduction on a paystub
   * PUT /payroll/paystubs/:paystubId/deductions/:index?type=pretax|posttax|garnishment
   *
   * @param paystubId - The paystub ID
   * @param index - The index of the deduction in its array (0-based)
   * @param type - The type of deduction: 'pretax', 'posttax', or 'garnishment'
   * @param request - The updated deduction data
   */
  async updateDeduction(
    paystubId: string,
    index: number,
    type: 'pretax' | 'posttax' | 'garnishment',
    request: UpdatePaystubDeductionRequest,
  ): Promise<DeductionResponse> {
    try {
      const response = await this.client.put<DeductionResponse>(
        `/payroll/paystubs/${paystubId}/deductions/${index}?type=${type}`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error updating deduction:', paystubId, index, type, error)
      throw error
    }
  }

  /**
   * Remove a deduction from a paystub
   * DELETE /payroll/paystubs/:paystubId/deductions/:index?type=pretax|posttax|garnishment
   *
   * @param paystubId - The paystub ID
   * @param index - The index of the deduction in its array (0-based)
   * @param type - The type of deduction: 'pretax', 'posttax', or 'garnishment'
   * @returns The updated paystub after removing the deduction
   */
  async removeDeduction(
    paystubId: string,
    index: number,
    type: 'pretax' | 'posttax' | 'garnishment',
  ): Promise<PaystubResponse> {
    try {
      const response = await this.client.delete<PaystubResponse>(
        `/payroll/paystubs/${paystubId}/deductions/${index}?type=${type}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error removing deduction:', paystubId, index, type, error)
      throw error
    }
  }
}

// Export a singleton instance
export const paystubsApi = new PaystubsApi()

export default paystubsApi
